// this service is to enable backwards compatibility for networks that were made before some issues were fixed
import { getDefaultUnbalancedPercent } from "../utils/consumerDefaults";
import { getNewCableSourceId } from "../utils/cableFunctions";
import { getConductorSourceIdEnabled } from "../utils/referenceFunctions";
import { LatLng } from "../model/viewModel/simpleTypes";
import * as phase from "../constants/phase";

export const cleanseDownstreamFeederNumbers = (network) => {
  const processedCableIds = [];
  const feeders = network.cables.filter((f) => f.isFirstLeg !== "false");
  feeders.forEach((feeder) => {
    processedCableIds.push(feeder.id);
  });
  feeders.forEach((feeder) => {
    setDownstreamFeederNumberRecursive(feeder, network, processedCableIds);
  });
};

export const setNewDefaults = (network, reference) => {
  network.groupedConnections.forEach((groupedConnection) => {
    groupedConnection.groupedConnectionPoints.forEach((groupedConnectionPoint) => {
      if (groupedConnectionPoint.lowLoadedPhase === undefined) {
        groupedConnectionPoint.lowLoadedPhase = "L1";
      }

      if (groupedConnectionPoint.unbalancePercent === undefined) {
        groupedConnectionPoint.unbalancePercent = getDefaultUnbalancedPercent(
          {
            transformerProperties: {},
            network: network,
          },
          network.transformers,
          network.cables,
          groupedConnection.id,
        );
      }
    });
  });

  if (network.transformers) {
    network.transformers.forEach((transformer) => {
      if (!transformer.designVoltage) {
        transformer.designVoltage = reference.constraints.systemVoltage;
      }
    });
  }
};

const setDownstreamFeederNumberRecursive = (upstreamCable, network, processedCableIds) => {
  const connectedCables = network.cables.filter(
    (c) =>
      c.startAssetId === upstreamCable.endAssetId &&
      processedCableIds.findIndex((p) => c.id === p) === -1,
  );

  if (!connectedCables) {
    return;
  }

  connectedCables.forEach((cable) => {
    processedCableIds.push(cable.id);

    cable.feederNumber = upstreamCable.feederNumber;
    setDownstreamFeederNumberRecursive(cable, network, processedCableIds);
  });
};

// Fix for issue documented in ticket 45702
// Studies may have been saved with incorrect phaseAutoConsumers & phaseMap properties
// This is for polyphase consumers only
export const fixAutoPhaseProperties = (network) => {
  network.groupedConnections
    ?.flatMap((gc) => gc.groupedConnectionPoints)
    ?.filter((gcp) => !!gcp)
    .forEach((gcp) => {
      const isPolyPhase = gcp.numberOfPhases !== phase.SINGLE;
      const phase1count = parseInt(gcp.phase1Consumers || 0);
      const phase2count = parseInt(gcp.phase2Consumers || 0);
      const phase3count = parseInt(gcp.phase3Consumers || 0);
      const autoCount = parseInt(gcp.phaseAutoConsumers || 0);
      const count = parseInt(gcp.count || 0);

      if (
        isPolyPhase &&
        phase1count === 0 &&
        phase2count === 0 &&
        phase3count === 0 &&
        autoCount > count &&
        count > 0
      ) {
        gcp.phaseAutoConsumers = count;
        gcp.phaseMap = [count, 0, 0, 0];
      }
    });
};

// Fix for issue documented in ticket 46517
// Studies may have been saved with incorrect phase{N}Consumers properties
// This is for single phase consumers only
export const fixManualPhaseProperties = (network) => {
  network.groupedConnections
    ?.flatMap((gc) => gc.groupedConnectionPoints)
    ?.filter((gcp) => !!gcp)
    .forEach((gcp) => {
      const isSinglePhase = gcp.numberOfPhases === phase.SINGLE;

      if (isSinglePhase && gcp.phaseMap?.length === 4) {
        gcp.phaseAutoConsumers = gcp.phaseMap[0];
        gcp.phase1Consumers = gcp.phaseMap[1];
        gcp.phase2Consumers = gcp.phaseMap[2];
        gcp.phase3Consumers = gcp.phaseMap[3];
      }
    });
};

export const fixTransformerPrimaryVoltage = (network, reference) => {
  network.transformers.forEach((transformer) => {
    if (!transformer.primaryVoltagekV) {
      transformer.primaryVoltagekV =
        reference.transformers.defaults[`${transformer.mounting}DefaultPrimaryVoltagekV`];
      transformer.primaryVoltagekVIsDefault = false;
    } else if (transformer.primaryVoltagekVIsDefault === undefined) {
      transformer.primaryVoltagekVIsDefault = false;
    }
  });
};

// Fix for issue documented in ticket 47346
// Studies may have been saved with nameplateRating not set to auto when autoSelect is true
export const fixTransformerAutoSelect = (network) => {
  network.transformers.forEach((transformer) => {
    if (
      (transformer.autoSelect === true || transformer.autoSelect === "true") &&
      transformer.nameplateRating !== "auto"
    ) {
      transformer.nameplateRating = "auto";
    }
  });
};

// Fix for issue documented in ticket 47443
// Studies may have been saved with invalid geometries on nodes
export const fixInvalidGeometries = (network) => {
  network.groupedConnections.forEach((gc) => {
    if ((gc.geometry?.lat == null || gc.geometry?.lng == null) && gc.geometry?.length === 2) {
      var lat = parseFloat(gc.geometry[0]);
      var lng = parseFloat(gc.geometry[1]);
      if (!!lat && !!lng) {
        gc.geometry = new LatLng(lat, lng);
      }
    }
  });
};

export const assignConductorSourceIds = (network, clientSettings) => {
  if (getConductorSourceIdEnabled(clientSettings)) {
    const existingSourceIds = network.cables.map((c) => c.wpdId).filter((id) => !!id);
    network.cables.forEach((cable) => {
      if (!cable.wpdId) {
        const newSourceId = getNewCableSourceId(existingSourceIds);
        cable.wpdId = newSourceId;
        existingSourceIds.push(newSourceId);
      }
    });
  }
};

// Fix for issue documented in ticket 49258
// Studies may have been saved with cables phase set to "Split" when they should be "Two"
export const fixInvalidCablePhases = (network) => {
  network.cables.forEach((c) => {
    if (c.numberOfPhases === phase.SPLIT) {
      c.numberOfPhases = phase.TWO;
    }
  });
};
